import type { CharacterIndexSourceAnchor } from "./types.js";
import {
  mapValidatedWindowRangeToSourceAnchors,
  type PreparedSingleWitnessCharacterExtractionCall
} from "./extractionPreparation.js";

export interface SingleWitnessCharacterMentionEvidence {
  readonly name: string;
  readonly transliteration?: string | null;
  readonly window: string;
  readonly role?: string | null;
}

export type GroundedCharacterMentionStatus = "grounded" | "ambiguous" | "not_found";

export interface CharacterMentionTextRange {
  readonly start: number;
  readonly end: number;
}

export interface GroundedCharacterMentionDiagnostic {
  readonly code: "empty-window" | "window-not-found" | "ambiguous-window";
  readonly message: string;
}

export interface GroundedSingleWitnessCharacterMention extends SingleWitnessCharacterMentionEvidence {
  readonly status: GroundedCharacterMentionStatus;
  readonly matchedRange?: CharacterMentionTextRange;
  readonly candidateRanges: readonly CharacterMentionTextRange[];
  readonly sourceAnchors: readonly CharacterIndexSourceAnchor[];
  readonly diagnostics: readonly GroundedCharacterMentionDiagnostic[];
}

export interface GroundSingleWitnessCharacterMentionsOptions {
  readonly maxCandidateRanges?: number;
}

const findExactRanges = (
  sourceText: string,
  window: string
): CharacterMentionTextRange[] => {
  if (!window) {
    return [];
  }

  const ranges: CharacterMentionTextRange[] = [];
  let cursor = 0;
  while (cursor <= sourceText.length) {
    const start = sourceText.indexOf(window, cursor);
    if (start < 0) {
      break;
    }
    const end = start + window.length;
    ranges.push({ start, end });
    cursor = start + 1;
  }
  return ranges;
};

const limitCandidateRanges = (
  ranges: readonly CharacterMentionTextRange[],
  options: GroundSingleWitnessCharacterMentionsOptions
): readonly CharacterMentionTextRange[] => {
  if (typeof options.maxCandidateRanges !== "number") {
    return ranges;
  }
  return ranges.slice(0, Math.max(0, Math.floor(options.maxCandidateRanges)));
};

const normalizeMention = (
  mention: SingleWitnessCharacterMentionEvidence
): SingleWitnessCharacterMentionEvidence => ({
  name: mention.name,
  ...(mention.transliteration !== undefined ? { transliteration: mention.transliteration } : {}),
  window: mention.window,
  ...(mention.role !== undefined ? { role: mention.role } : {})
});

export const groundSingleWitnessCharacterMentions = (
  call: Pick<PreparedSingleWitnessCharacterExtractionCall, "sourceText" | "sourceMap">,
  mentions: readonly SingleWitnessCharacterMentionEvidence[],
  options: GroundSingleWitnessCharacterMentionsOptions = {}
): GroundedSingleWitnessCharacterMention[] => (
  mentions.map((mention) => {
    const normalizedMention = normalizeMention(mention);
    if (mention.window.length === 0) {
      return {
        ...normalizedMention,
        status: "not_found",
        candidateRanges: [],
        sourceAnchors: [],
        diagnostics: [{
          code: "empty-window",
          message: "Mention window is empty and cannot be grounded."
        }]
      };
    }

    const ranges = findExactRanges(call.sourceText, mention.window);
    const candidateRanges = limitCandidateRanges(ranges, options);
    if (ranges.length === 0) {
      return {
        ...normalizedMention,
        status: "not_found",
        candidateRanges,
        sourceAnchors: [],
        diagnostics: [{
          code: "window-not-found",
          message: "Mention window does not occur in the prepared sourceText."
        }]
      };
    }

    if (ranges.length > 1) {
      return {
        ...normalizedMention,
        status: "ambiguous",
        candidateRanges,
        sourceAnchors: [],
        diagnostics: [{
          code: "ambiguous-window",
          message: "Mention window occurs more than once in the prepared sourceText."
        }]
      };
    }

    const matchedRange = ranges[0]!;
    return {
      ...normalizedMention,
      status: "grounded",
      matchedRange,
      candidateRanges,
      sourceAnchors: mapValidatedWindowRangeToSourceAnchors(call, matchedRange),
      diagnostics: []
    };
  })
);
